import React, { useState, useEffect } from 'react';
import { Box, Button, Drawer, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, Typography, FormGroup, Checkbox, TextField, MenuItem, Select, InputLabel, FormControl, Switch, Avatar } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { updateCatchVisibility, fetchFishSpecies, fetchNotificationSettings, updateNotificationSettings, testPushNotification, listBuddies } from './api';
import AdminMenu from './views/AdminMenu';
import NotificationBell from './components/NotificationBell';
import { usePushNotifications } from './usePushNotifications';
import { API_URL, DEFAULT_PB_SPECIES } from './config';

export default function MainMenu({ sx, onEditProfile }) {
  const [open, setOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [privacyError, setPrivacyError] = useState('');
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications();
  const navigate = useNavigate();
  const { user, token, login, logout } = useAuth();
  const [selectedVisibility, setSelectedVisibility] = useState(user?.catch_visibility || 'everyone');
   const [speciesOptions, setSpeciesOptions] = useState([]);
   const [pbSpecies, setPbSpecies] = useState(
    Array.isArray(user?.pb_species_selection) && user.pb_species_selection.length
      ? user.pb_species_selection
      : DEFAULT_PB_SPECIES,
  );
  const [notifyOnCatch, setNotifyOnCatch] = useState('buddies');
  const [nearbyRadiusKm, setNearbyRadiusKm] = useState(10);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [buddies, setBuddies] = useState([]);
  const [selectedBuddyIds, setSelectedBuddyIds] = useState([]);
  const [buddyAdminIds, setBuddyAdminIds] = useState([]);
  const [activeFrom, setActiveFrom] = useState('');
  const [activeTo, setActiveTo] = useState('');
  const [createGroupError, setCreateGroupError] = useState('');
  const groups = Array.isArray(user?.groups) ? user.groups : [];
  const isAdmin = groups.some((g) => g.role === 'admin' && (g.group_name || '').toLowerCase() === 'admins');

  useEffect(() => {
    if (!token) return;
    fetchFishSpecies(token)
      .then((items) => {
        if (!Array.isArray(items)) return;
        const sorted = items.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        setSpeciesOptions(sorted);
      })
      .catch(() => {
        setSpeciesOptions([]);
      });
    // Load notification settings
    fetchNotificationSettings(token)
      .then((settings) => {
        setNotifyOnCatch(settings.notify_on_catch || 'buddies');
        setNearbyRadiusKm(settings.nearby_radius_km || 10);
      })
      .catch(() => {});
    // Load buddies
    listBuddies(token)
      .then((data) => setBuddies(Array.isArray(data) ? data : []))
      .catch(() => setBuddies([]));
  }, [token]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', ...sx }}>
        <NotificationBell />
        <IconButton
          color="primary"
          aria-label="open menu"
          onClick={() => setOpen(true)}
        >
          <MenuIcon fontSize="large" />
        </IconButton>
      </Box>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
      >
        <Box sx={{ width: 250, p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {isAdmin && <AdminMenu />}
          {onEditProfile && (
            <Button
              variant="text"
              fullWidth
              sx={{ mt: isAdmin ? 2 : 0, mb: 1 }}
              onClick={() => {
                setOpen(false);
                onEditProfile();
              }}
            >
              Change personal data
            </Button>
          )}
          <Button
            variant="text"
            fullWidth
            sx={{ mb: 1 }}
            onClick={() => {
              setOpen(false);
              setSelectedVisibility(user?.catch_visibility || 'everyone');
              const currentPb =
                Array.isArray(user?.pb_species_selection) && user.pb_species_selection.length
                  ? user.pb_species_selection
                  : DEFAULT_PB_SPECIES;
              setPbSpecies(currentPb.slice(0, 4));
              setPrivacyError('');
              setPrivacyOpen(true);
            }}
          >
            Privacy settings
          </Button>
          <Button
            variant="text"
            fullWidth
            sx={{ mb: 1 }}
            onClick={() => {
              setOpen(false);
              navigate('/tackle');
            }}
          >
            Tackle Manager
          </Button>
          <Button
            variant="text"
            fullWidth
            sx={{ mb: 1 }}
            onClick={() => {
              setOpen(false);
              setCreateGroupOpen(true);
            }}
          >
            Add Leaderboard
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={() => {
              setOpen(false);
              logout();
            }}
          >
            Logout
          </Button>
        </Box>
      </Drawer>
      <Dialog open={privacyOpen} onClose={() => savingPrivacy ? null : setPrivacyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Catch visibility</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose who can see your catches in your profile and other views.
          </Typography>
          <RadioGroup
            value={selectedVisibility}
            onChange={(e) => setSelectedVisibility(e.target.value)}
          >
            <FormControlLabel value="everyone" control={<Radio />} label="Everyone" />
            <FormControlLabel value="buddies" control={<Radio />} label="Buddies only" />
            <FormControlLabel value="none" control={<Radio />} label="Nobody (only me)" />
          </RadioGroup>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Personal best species
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Select up to 4 species that should be highlighted in your profile stats.
            </Typography>
            <FormGroup>
              {speciesOptions.map((s) => (
                <FormControlLabel
                  key={s.id ?? s.name}
                  control={
                    <Checkbox
                      checked={pbSpecies.includes(s.name)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const name = s.name;
                        setPbSpecies((prev) => {
                          if (checked) {
                            if (prev.includes(name)) return prev;
                            if (prev.length >= 4) return prev;
                            return [...prev, name];
                          }
                          return prev.filter((n) => n !== name);
                        });
                      }}
                    />
                  }
                  label={s.name}
                />
              ))}
            </FormGroup>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Notification settings
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              When you add a catch, who should be notified?
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Notify</InputLabel>
              <Select
                value={notifyOnCatch}
                label="Notify"
                onChange={(e) => setNotifyOnCatch(e.target.value)}
              >
                <MenuItem value="buddies">My buddies only</MenuItem>
                <MenuItem value="nearby">Nearby users only</MenuItem>
                <MenuItem value="both">Both buddies and nearby users</MenuItem>
                <MenuItem value="nobody">Nobody</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Nearby radius (km)"
              type="number"
              size="small"
              fullWidth
              value={nearbyRadiusKm}
              onChange={(e) => setNearbyRadiusKm(Number(e.target.value) || 10)}
              inputProps={{ min: 1, max: 500 }}
              helperText="Receive notifications when someone catches fish within this distance"
            />
            {pushSupported && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2">Push notifications</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Get notified even when the app is closed
                  </Typography>
                </Box>
                <Switch
                  checked={pushSubscribed}
                  onChange={async (e) => {
                    if (e.target.checked) {
                      await subscribePush();
                    } else {
                      await unsubscribePush();
                    }
                  }}
                />
              </Box>
            )}
            {pushSupported && pushSubscribed && (
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={async () => {
                  try {
                    const result = await testPushNotification(token);
                    if (result.ok) {
                      alert(`Test push sent! (${result.subscriptions_count} subscription(s))`);
                    } else {
                      alert(result.error || 'Failed to send test push');
                    }
                  } catch (err) {
                    alert('Error sending test push: ' + err.message);
                  }
                }}
              >
                Send Test Push
              </Button>
            )}
          </Box>
          {privacyError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {privacyError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => savingPrivacy ? null : setPrivacyOpen(false)} disabled={savingPrivacy}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!user || !token) return;
              if (!pbSpecies || pbSpecies.length === 0) {
                setPrivacyError('Please select at least one species for your PB stats.');
                return;
              }
              setSavingPrivacy(true);
              setPrivacyError('');
              try {
                const updated = await updateCatchVisibility(token, selectedVisibility, pbSpecies.slice(0, 4));
                // Also save notification settings
                await updateNotificationSettings(token, {
                  notify_on_catch: notifyOnCatch,
                  nearby_radius_km: nearbyRadiusKm,
                });
                if (login) {
                  login(updated, token);
                }
                setPrivacyOpen(false);
              } catch (e) {
                setPrivacyError(e.message || 'Failed to save settings');
              } finally {
                setSavingPrivacy(false);
              }
            }}
            variant="contained"
            disabled={savingPrivacy}
          >
            {savingPrivacy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={createGroupOpen} onClose={() => setCreateGroupOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Group</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Group Name"
              fullWidth
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              required
            />
            <Typography variant="subtitle2">Invite buddies (toggle admin)</Typography>
            <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
              {buddies.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No buddies yet.</Typography>
              ) : (
                buddies.map((u) => (
                  <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={(() => {
                          const pic = u?.profile_picture;
                          if (!pic) return undefined;
                          if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                          const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                          return `${API_URL}/${normalized}`;
                        })()}
                        sx={{ width: 28, height: 28 }}
                      />
                      <Typography variant="body2">{u.nickname}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                      size="small"
                      variant={selectedBuddyIds.includes(u.id) ? 'contained' : 'outlined'}
                      onClick={() => {
                        setSelectedBuddyIds((prev) => (
                          prev.includes(u.id) ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                        ));
                      }}
                      >
                        {selectedBuddyIds.includes(u.id) ? 'Selected' : 'Select'}
                      </Button>
                      <Button
                        size="small"
                        variant={buddyAdminIds.includes(u.id) ? 'contained' : 'outlined'}
                        onClick={() => {
                          setBuddyAdminIds((prev) => (
                            prev.includes(u.id) ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                          ));
                        }}
                        disabled={!selectedBuddyIds.includes(u.id)}
                      >
                        Admin
                      </Button>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
            <Typography variant="subtitle2">Active time range</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={activeFrom}
                onChange={(e) => setActiveFrom(e.target.value)}
              />
              <TextField
                label="End"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={activeTo}
                onChange={(e) => setActiveTo(e.target.value)}
              />
            </Box>
            {createGroupError && (
              <Typography variant="body2" color="error">{createGroupError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateGroupOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newGroupName.trim()}
            onClick={async () => {
              setCreateGroupError('');
              try {
                // Convert datetime-local strings to ISO if provided
                const payload = { name: newGroupName.trim() };
                if (activeFrom) payload.active_from = new Date(activeFrom).toISOString();
                if (activeTo) payload.active_to = new Date(activeTo).toISOString();
                const res = await fetch(`${API_URL}/groups`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                  body: JSON.stringify(payload),
                });
                if (!res.ok) throw new Error('Failed to create group');
                const created = await res.json();
                const newId = created?.id;
                const authHeaders = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
                for (const uid of selectedBuddyIds) {
                  await fetch(`${API_URL}/groups/${newId}/join?user_id=${uid}`, { method: 'POST', headers: authHeaders });
                  if (buddyAdminIds.includes(uid)) {
                    await fetch(`${API_URL}/groups/${newId}/assign-role?user_id=${uid}&role=admin`, { method: 'POST', headers: authHeaders });
                  }
                }
                setCreateGroupOpen(false);
                setNewGroupName('');
                setSelectedBuddyIds([]);
                setBuddyAdminIds([]);
                setActiveFrom('');
                setActiveTo('');
                // Navigate to dashboard to see the new leaderboard
                navigate('/dashboard');
              } catch (err) {
                setCreateGroupError('Failed to create group');
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
